import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, MessageCircle, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { sanitizeText } from "@/utils/validation";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"

interface Comment {
  id: string;
  comment_text: string;
  commenter_name: string | null;
  created_at: string;
}

export default function ViewSharedResume() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [resumeContent, setResumeContent] = useState<any>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [commenterName, setCommenterName] = useState("");
  const [sharedResumeId, setSharedResumeId] = useState<string>("");
  const [allowComments, setAllowComments] = useState(true);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const startTimeRef = useRef<number>(Date.now());
  const viewTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    loadSharedResume();
    startTimeRef.current = Date.now();

    // Track view when leaving page
    const handleBeforeUnload = () => {
      if (sharedResumeId && !viewTrackedRef.current) {
        trackView();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also track when component unmounts (navigation)
      if (sharedResumeId && !viewTrackedRef.current) {
        trackView();
      }
    };
  }, [token]);

  // Track view after sharedResumeId is set
  useEffect(() => {
    if (sharedResumeId && !viewTrackedRef.current) {
      // Track initial view after 5 seconds (to filter bots and accidental clicks)
      const timer = setTimeout(() => {
        trackView();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [sharedResumeId]);

  const trackView = async () => {
    if (!sharedResumeId || viewTrackedRef.current) return;

    viewTrackedRef.current = true;
    const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);

    try {
      await supabase.functions.invoke('track-resume-view', {
        body: {
          sharedResumeId,
          timeSpentSeconds: timeSpent
        }
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const loadSharedResume = async () => {
    try {
      const { data: sharedResume, error: shareError } = await supabase
        .from("shared_resumes")
        .select("id, resume_id, allow_comments")
        .eq("share_token", token)
        .single();

      if (shareError) throw shareError;

      setSharedResumeId(sharedResume.id);
      setAllowComments(sharedResume.allow_comments);

      const { data: resume, error: resumeError } = await supabase
        .from("saved_resumes")
        .select("*")
        .eq("id", sharedResume.resume_id)
        .single();

      if (resumeError) throw resumeError;

      setResumeContent(resume);
      loadComments(sharedResume.id);
    } catch (error: any) {
      toast.error("Failed to load resume");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!resumeContent) return;
    const fetchUserSettings = async () => {
      try {
        const { data: userSettings, error: settingsError } = await supabase
          .from("profiles")
          .select("comments_enabled, comments_hidden")
          .eq("user_id", resumeContent.user_id)
          .single();
        if (settingsError) throw settingsError;
        const commentsEnabled = userSettings.comments_enabled;
        const commentsHidden = userSettings.comments_hidden;

        setAllowComments(commentsEnabled);
        setShowComments(!commentsHidden);
      } catch (error) {
        // Silently handle error
      }
    };
    fetchUserSettings();
  }, [resumeContent]);

  const loadComments = async (resumeId: string) => {
    const { data, error } = await supabase
      .from("resume_comments")
      .select("*")
      .eq("shared_resume_id", resumeId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error loading comments:", error);
      return;
    }

    setComments(data || []);
  };

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    const commentWordCount = countWords(newComment);
    if (commentWordCount > 100) {
      toast.error(`Comment is too long (${commentWordCount} words). Maximum is 100 words.`);
      return;
    }

    if (newComment.length > 500) {
      toast.error("Comment is too long. Maximum is 500 characters.");
      return;
    }

    if (commenterName && commenterName.trim()) {
      const nameWordCount = countWords(commenterName);
      if (nameWordCount > 2) {
        toast.error(`Name is too long (${nameWordCount} words). Maximum is 2 words.`);
        return;
      }

      if (commenterName.length > 50) {
        toast.error("Name is too long. Maximum is 50 characters.");
        return;
      }
    }

    const sanitizedComment = sanitizeText(newComment.trim());
    const sanitizedName = commenterName ? sanitizeText(commenterName.trim()) : null;

    try {
      const { error } = await supabase.from("resume_comments").insert({
        shared_resume_id: sharedResumeId,
        comment_text: sanitizedComment,
        commenter_name: sanitizedName,
      });

      if (error) throw error;

      try {
        await supabase.functions.invoke("send-comment-notification", {
          body: {
            sharedResumeId,
            commenterName: sanitizedName || "Anonymous",
            commentText: sanitizedComment,
            shareToken: token,
          },
        });
      } catch (emailError) {
        console.error("Failed to send email notification:", emailError);
      }

      toast.success("Comment added!");
      setNewComment("");
      setCommenterName("");
      setShowCommentForm(false);
      loadComments(sharedResumeId);
    } catch (error: any) {
      toast.error("Failed to add comment");
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!resumeContent) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">{AutoTranslate("Resume not found")}</p>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#07101a] px-3 py-8 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <section className="mx-auto mb-8 w-full max-w-[816px] rounded-md bg-white px-5 py-8 text-black shadow-2xl ring-1 ring-black/10 aspect-[8.5/11] min-h-[640px] sm:px-14 sm:py-12 lg:min-h-[1056px]">
          <div className="mx-auto max-w-[680px] text-[10px] leading-relaxed sm:text-xs">
            <header className="border-b border-black pb-4 text-center">
              <h1 className="text-xl font-semibold tracking-normal sm:text-2xl">
                {resumeContent.content?.personalInfo?.name || resumeContent.title}
              </h1>
              <p className="mt-2 text-[9px] text-black/75 sm:text-[10px]">
                {[resumeContent.content?.personalInfo?.email, resumeContent.content?.personalInfo?.phone]
                  .filter(Boolean)
                  .join("  •  ")}
              </p>
            </header>

            {resumeContent.content?.summary && (
              <section className="mt-6">
                <h2 className="mb-3 text-center text-xs font-bold uppercase tracking-wide">
                  {AutoTranslate("Summary")}
                </h2>
                <p>{resumeContent.content.summary}</p>
              </section>
            )}

            {resumeContent.content?.education?.length > 0 && (
              <section className="mt-6">
                <h2 className="mb-3 text-center text-xs font-bold uppercase tracking-wide">
                  {AutoTranslate("Education")}
                </h2>
                <div className="space-y-3">
                  {resumeContent.content.education.map((edu: any, i: number) => (
                    <div key={i}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-bold">{edu.institution}</p>
                          <p className="italic">{edu.degree}</p>
                        </div>
                        {edu.year && <p className="shrink-0 text-right">{edu.year}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {resumeContent.content?.experience?.length > 0 && (
              <section className="mt-6">
                <h2 className="mb-3 text-center text-xs font-bold uppercase tracking-wide">
                  {AutoTranslate("Experience")}
                </h2>
                <div className="space-y-4">
                  {resumeContent.content.experience.map((exp: any, i: number) => (
                    <div key={i}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-bold">{exp.company}</p>
                          <p className="italic">{exp.role}</p>
                        </div>
                        <p className="shrink-0 text-right">
                          {[exp.start, exp.end].filter(Boolean).join(" - ")}
                        </p>
                      </div>
                      {exp.description && (
                        <p className="mt-2 whitespace-pre-line">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {resumeContent.content?.skills?.length > 0 && (
              <section className="mt-6">
                <h2 className="mb-3 text-center text-xs font-bold uppercase tracking-wide">
                  {AutoTranslate("Skills")}
                </h2>
                <p>{resumeContent.content.skills.join(", ")}</p>
              </section>
            )}
          </div>
        </section>
      </div>

      {showComments && (
        <Card className="mx-auto max-w-[816px] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              {t("share.comments")} ({comments.length})
            </h3>
            {allowComments && (
              <Button
                onClick={() => setShowCommentForm(!showCommentForm)}
                size="sm"
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("share.addComment")}
              </Button>
            )}
          </div>

          {allowComments && showCommentForm && (
            <div className="mb-6 space-y-3 p-4 border rounded-lg">
              <Input
                placeholder="Your name (optional)"
                value={commenterName}
                onChange={(e) => setCommenterName(e.target.value)}
              />
              <Textarea
                placeholder={AutoTranslate("Write your comment...")}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
              />
              <div className="flex gap-2">
                <Button onClick={handleAddComment}>{AutoTranslate("Post Comment")}</Button>
                <Button variant="outline" onClick={() => setShowCommentForm(false)}>
                  {AutoTranslate("Cancel")}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="border-b pb-4 last:border-0">
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-medium text-sm">
                    {comment.commenter_name || t("share.anonymous")}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {new Date(comment.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm">{comment.comment_text}</p>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                {allowComments ? "No comments yet. Be the first to comment!" : "Comments are disabled for this resume."}
              </p>
            )}
          </div>
        </Card>
      )}
    </main>
  );
}
